// One record rendered as a compact card (mobile-style transaction list). A port
// of the Android `TransactionRecordCard`: an ID badge + bold title + category
// chip header row, then a wrap grid of label/value detail cells. A designer
// "Display as" format always wins over the plain defaults (rendered by the
// shared <DisplayCell>, the same one the desktop grid uses).
//
// When the form HAS a feed view, that is the card instead: the designer has
// said in so many words how one record of this form reads, and the heuristics
// above exist only because nobody had. Same layout the process trail draws,
// through the same <FeedRows>, so a record looks like itself everywhere.

import type { ReactNode } from 'react'
import type { ColumnDef, SqlModel } from '../types'
import { columnFormKey, visibleColumns } from '../utils'
import { DisplayCell, hasDisplayFormat } from '../displayFormat'
import { personLabel, usePeople } from '../people'
import { FeedRows } from '../flow/FeedCard'
import { feedRowsFor } from '../flow/feedLayout'
import { cardParts, compactCellValue, compactRawValue } from './heuristics'

interface Props {
  sm: SqlModel
  row: Record<string, unknown>
  onClick: () => void
  // Trailing header slot — the row's ⋮ actions menu (and/or a status chip).
  trailing?: ReactNode
}

export default function CompactRecordCard({ sm, row, onClick, trailing }: Props) {
  const feed = feedRowsFor(sm, row)
  const { idCol, titleCol, catCol, detailCols } = cardParts(sm, row)
  const allCols = visibleColumns(sm)

  const idText = compactCellValue(row, idCol)
  const titleFormatted = titleCol && hasDisplayFormat(titleCol)
  const catText = compactCellValue(row, catCol)
  // A user-key column reads as the person (see `txn/people`); <DisplayCell>
  // applies the same rule for the formatted branches.
  const people = usePeople()
  const titlePerson = titleCol
    && personLabel(people, columnFormKey(titleCol), compactRawValue(row, titleCol))
  const catPerson = catCol
    && personLabel(people, columnFormKey(catCol), compactRawValue(row, catCol))

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() }
      }}
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 12,
        padding: 15,
        background: 'var(--color-bg)',
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-secondary)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg)' }}
    >
      {feed ? (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <FeedRows rows={feed} scale={1.06} />
          </div>
          {trailing && (
            <span onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
              {trailing}
            </span>
          )}
        </div>
      ) : (
      <>
      {/* Header row: ID badge → title → category chip → trailing (⋮). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {idCol && idText && !hasDisplayFormat(idCol) && (
          <span style={idBadgeStyle}>ID {idText}</span>
        )}
        <span style={{
          flex: 1, minWidth: 0,
          fontSize: '1.0125rem', fontWeight: 800, color: 'var(--color-text)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {titleCol
            ? (titlePerson
                || (titleFormatted
                  ? <DisplayCell value={compactRawValue(row, titleCol)} col={titleCol} row={row} columns={allCols} />
                  : (compactCellValue(row, titleCol) ?? '—')))
            : '—'}
        </span>
        {catCol && (catText || catPerson || hasDisplayFormat(catCol)) && (
          catPerson
            ? <span style={categoryChipStyle}>{catPerson}</span>
            : hasDisplayFormat(catCol)
              ? <DisplayCell value={compactRawValue(row, catCol)} col={catCol} row={row} columns={allCols} />
              : <span style={categoryChipStyle}>{catText}</span>
        )}
        {trailing && (
          <span onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
            {trailing}
          </span>
        )}
      </div>

      {/* Detail grid: uppercase muted label + value, wrapping. */}
      {detailCols.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 24, rowGap: 11 }}>
          {detailCols.map(col => (
            <DetailCell key={col.col_id || col.name} sm={sm} row={row} col={col} allCols={allCols} />
          ))}
        </div>
      )}
      </>
      )}
    </div>
  )
}

function DetailCell({ sm, row, col, allCols }: {
  sm: SqlModel
  row: Record<string, unknown>
  col: ColumnDef
  allCols: ColumnDef[]
}) {
  void sm
  const formatted = hasDisplayFormat(col)
  const text = compactCellValue(row, col)
  const person = personLabel(usePeople(), columnFormKey(col), compactRawValue(row, col))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{
        fontSize: '0.8125rem', fontWeight: 700, letterSpacing: 0.3,
        textTransform: 'uppercase', color: 'var(--color-text-secondary)',
      }}>
        {col.heading || col.name}
      </span>
      <span style={{
        fontSize: '1.0025rem', fontWeight: 600, color: 'var(--color-text)',
      }}>
        {person
          || (formatted
            ? <DisplayCell value={compactRawValue(row, col)} col={col} row={row} columns={allCols} />
            : (text ?? '—'))}
      </span>
    </div>
  )
}

const idBadgeStyle: React.CSSProperties = {
  flexShrink: 0,
  display: 'inline-flex', alignItems: 'center',
  padding: '2px 8px', borderRadius: 8,
  background: 'var(--color-bg-secondary)',
  color: 'var(--color-text-secondary)',
  fontSize: '0.8125rem', fontWeight: 700, whiteSpace: 'nowrap',
}

const categoryChipStyle: React.CSSProperties = {
  flexShrink: 0,
  display: 'inline-flex', alignItems: 'center',
  padding: '2px 10px', borderRadius: 12,
  background: 'var(--color-accent-weak, rgba(76,141,255,0.16))',
  color: 'var(--color-accent, #4c8dff)',
  fontSize: '0.8125rem', fontWeight: 700, whiteSpace: 'nowrap',
}
